import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import RemoveIcon from "@mui/icons-material/Remove";
import { useTranslation } from "react-i18next";
import { listPlatformDominios } from "../../../api/client";
import { postPlatformDominioPassengerPartnerReportAudit } from "../../../api/passagensApi";
import { resolveTenantDatabaseName } from "../../../utils/resolveTenantDatabaseName";
import { AnalyticsDataTable, AnalyticsSection } from "./platformConsoleAnalyticsUi";
import ConsoleOperationSteps, { useOperationProgress } from "./ConsoleOperationSteps";

const STATUS_COLOR = {
  ok: "success",
  warning: "warning",
  error: "error",
};

function AuditFilterChip({ filterKey, active, count, label, color = "default", onClick, disabled = false }) {
  if (disabled || count == null || count <= 0) {
    return <Chip size="small" variant="outlined" label={label} />;
  }
  return (
    <Chip
      size="small"
      clickable
      color={active ? color : "default"}
      variant={active ? "filled" : "outlined"}
      label={label}
      onClick={() => onClick(filterKey)}
      sx={{ cursor: "pointer" }}
    />
  );
}

function matchedRowType(item) {
  if (item.issues?.some((x) => x.type === "amount_mismatch")) return "amount_mismatch";
  const fieldIssue = (item.issues || []).find(
    (x) => !["amount_mismatch", "amount_payment_adjustment", "route_direction_swapped"].includes(x.type)
  );
  if (fieldIssue) return fieldIssue.type;
  if (item.match_confidence === "exact_id" || item.match_confidence === "high") return "matched";
  return "probable_match";
}

function matchedRowDetail(item, t) {
  if (item.issues?.some((x) => x.type === "amount_mismatch")) {
    const issue = item.issues.find((x) => x.type === "amount_mismatch");
    return `${fmtMoney(issue.report_amount)} × ${fmtMoney(issue.platform_amount)}`;
  }
  const payAdj = item.issues?.find((x) => x.type === "amount_payment_adjustment");
  if (payAdj) {
    return `${fmtMoney(payAdj.report_amount)} (ERP) × ${fmtMoney(payAdj.platform_valor_cobrado)} (cobrado)`;
  }
  return t(`platformPage.passengerPartnerAuditConfidence_${item.match_confidence}`, {
    score: item.match_score,
    defaultValue: `${item.match_confidence} (${item.match_score ?? "—"})`,
  });
}

function buildMatchedFilterKeys(item) {
  const keys = new Set(["matched", "report_rows", "platform_rows"]);
  if (item.match_confidence === "exact_id" || item.match_confidence === "high") {
    keys.add("exact_match");
  } else {
    keys.add("probable_match");
  }
  const hasAmountMismatch = item.issues?.some((x) => x.type === "amount_mismatch");
  const fieldIssue = (item.issues || []).find(
    (x) => !["amount_mismatch", "amount_payment_adjustment", "route_direction_swapped"].includes(x.type)
  );
  if (hasAmountMismatch) keys.add("amount_mismatch");
  if (fieldIssue) keys.add("field_mismatch");
  if (hasAmountMismatch || fieldIssue) keys.add("issues");
  return [...keys];
}
const SOURCE_OPTIONS = [
  { id: "praxio_luna", accept: ".pdf,application/pdf" },
  { id: "totalbus_rj", accept: ".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
];

const FILE_ACCEPT =
  ".pdf,.xls,.xlsx,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function detectSourceFromFile(file) {
  const name = String(file?.name || "").toLowerCase();
  if (name.endsWith(".xls") || name.endsWith(".xlsx")) return "totalbus_rj";
  if (name.endsWith(".pdf")) return "praxio_luna";
  return null;
}

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtCount(v) {
  return Number(v || 0).toLocaleString("pt-BR");
}

function fmtDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("pt-BR");
}

function fmtDateOnly(v) {
  if (!v) return null;
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return s;
  return d.toISOString().slice(0, 10);
}

function fmtTimeOnly(v) {
  if (!v) return null;
  const s = String(v);
  if (/^\d{2}:\d{2}/.test(s)) return s.slice(0, 5);
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function normPlace(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+-\s+[A-Z]{2}\s*$/i, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

function normName(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

function normSeat(value) {
  return String(value || "").trim().replace(/^0+/, "") || "";
}

function amountClose(a, b, tolerance = 0.05) {
  const x = Number(a);
  const y = Number(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  return Math.abs(x - y) <= tolerance;
}

function routesCompare(report, platform) {
  const ro = normPlace(report?.origem);
  const rd = normPlace(report?.destino);
  const po = normPlace(platform?.origem);
  const pd = normPlace(platform?.destino);
  if (!ro && !rd && !po && !pd) return "neutral";
  if (!ro || !rd || !po || !pd) return "partial";
  if (ro === po && rd === pd) return "match";
  if (ro === pd && rd === po) return "swapped";
  return "mismatch";
}

function formatRoute(origem, destino) {
  if (!origem && !destino) return null;
  return [origem, destino].filter(Boolean).join(" → ");
}

function compareScalar(a, b, { mode = "text" } = {}) {
  const emptyA = a == null || a === "";
  const emptyB = b == null || b === "";
  if (emptyA && emptyB) return "neutral";
  if (emptyA) return "platform_only";
  if (emptyB) return "report_only";
  if (mode === "date") return fmtDateOnly(a) === fmtDateOnly(b) ? "match" : "mismatch";
  if (mode === "time") return fmtTimeOnly(a) === fmtTimeOnly(b) ? "match" : "mismatch";
  if (mode === "seat") return normSeat(a) === normSeat(b) ? "match" : "mismatch";
  if (mode === "name") {
    const na = normName(a);
    const nb = normName(b);
    if (na === nb) return "match";
    if (na.includes(nb) || nb.includes(na)) return "match";
    return "mismatch";
  }
  if (mode === "id") {
    const sa = String(a).replace(/^0+/, "");
    const sb = String(b).replace(/^0+/, "");
    return sa === sb ? "match" : "mismatch";
  }
  return String(a).trim() === String(b).trim() ? "match" : "mismatch";
}

function compareAmountRow(report, platform) {
  const rt = Number(report?.total);
  if (!Number.isFinite(rt)) return { status: "neutral", hint: null };
  const cobrado = Number(platform?.valor_cobrado);
  const tarifa = Number(platform?.valor_tarifa);
  const acrescimo = Number(platform?.acrescimo_fp || 0);
  const desconto = Number(platform?.desconto_fp || 0);
  if (amountClose(rt, cobrado)) return { status: "match", hint: null };
  if (amountClose(rt, tarifa)) return { status: "match_fp", hint: "tarifa" };
  if (amountClose(rt, tarifa + acrescimo - desconto)) return { status: "match_fp", hint: "tarifa_fp" };
  if (!Number.isFinite(cobrado)) return { status: "report_only", hint: null };
  return { status: "mismatch", hint: null };
}

function buildComparisonRows(report, platform, t) {
  const rows = [];

  const push = (id, label, reportVal, platformVal, status, hint = null) => {
    if (status === "neutral" && reportVal == null && platformVal == null) return;
    rows.push({
      id,
      label,
      reportVal: reportVal ?? null,
      platformVal: platformVal ?? null,
      status,
      hint,
    });
  };

  const ticketReport = report?.ticket_key || report?.numero_sistema || report?.bilhete;
  const ticketPlatform = platform?.numero_sistema || platform?.localizador || platform?.numero_bpe;
  push(
    "ticket",
    t("platformPage.passengerPartnerAuditCompare_ticket"),
    ticketReport,
    ticketPlatform,
    compareScalar(ticketReport, ticketPlatform, { mode: "id" })
  );

  push(
    "localizador",
    t("platformPage.passengerPartnerAuditCompare_locator"),
    report?.localizador,
    platform?.localizador,
    compareScalar(report?.localizador, platform?.localizador)
  );

  push(
    "passenger",
    t("platformPage.passengerPartnerAuditCompare_passenger"),
    report?.passenger,
    platform?.nome_passageiro,
    compareScalar(report?.passenger, platform?.nome_passageiro, { mode: "name" })
  );

  push(
    "document",
    t("platformPage.passengerPartnerAuditCompare_document"),
    report?.document,
    platform?.documento_passageiro,
    compareScalar(report?.document, platform?.documento_passageiro, { mode: "id" })
  );

  push(
    "sale_date",
    t("platformPage.passengerPartnerAuditCompare_sale_date"),
    fmtDateOnly(report?.sale_date),
    fmtDateOnly(platform?.emitido_em),
    compareScalar(report?.sale_date, platform?.emitido_em, { mode: "date" })
  );

  push(
    "sale_time",
    t("platformPage.passengerPartnerAuditCompare_sale_time"),
    report?.sale_time || fmtTimeOnly(report?.sale_date),
    fmtTimeOnly(platform?.emitido_em),
    compareScalar(report?.sale_time || report?.sale_date, platform?.emitido_em, { mode: "time" })
  );

  push(
    "trip_date",
    t("platformPage.passengerPartnerAuditCompare_trip_date"),
    fmtDateOnly(report?.trip_date),
    fmtDateOnly(platform?.data_viagem),
    compareScalar(report?.trip_date, platform?.data_viagem, { mode: "date" })
  );

  push(
    "departure",
    t("platformPage.passengerPartnerAuditCompare_departure"),
    report?.departure_time,
    null,
    report?.departure_time ? "report_only" : "neutral"
  );

  const routeStatus = routesCompare(report, platform);
  push(
    "route",
    t("platformPage.passengerPartnerAuditCompare_route"),
    formatRoute(report?.origem, report?.destino),
    formatRoute(platform?.origem, platform?.destino),
    routeStatus,
    routeStatus === "swapped" ? t("platformPage.passengerPartnerAuditCompare_routeSwappedHint") : null
  );

  push(
    "seat",
    t("platformPage.passengerPartnerAuditCompare_seat"),
    report?.seat,
    platform?.poltrona,
    compareScalar(report?.seat, platform?.poltrona, { mode: "seat" })
  );

  if (report?.line_code) {
    push("line", t("platformPage.passengerPartnerAuditCompare_line"), report.line_code, null, "report_only");
  }

  const amountCmp = compareAmountRow(report, platform);
  push(
    "total",
    t("platformPage.passengerPartnerAuditCompare_total"),
    report?.total != null ? fmtMoney(report.total) : null,
    platform?.valor_cobrado != null ? fmtMoney(platform.valor_cobrado) : null,
    amountCmp.status,
    amountCmp.hint
      ? t(`platformPage.passengerPartnerAuditCompare_amountHint_${amountCmp.hint}`)
      : amountCmp.status === "mismatch" && platform?.valor_tarifa != null
        ? `${t("platformPage.passengerPartnerAuditDetail_valor_tarifa")}: ${fmtMoney(platform.valor_tarifa)}`
        : null
  );

  if (report?.tariff != null || platform?.valor_tarifa != null) {
    push(
      "tariff",
      t("platformPage.passengerPartnerAuditCompare_tariff"),
      report?.tariff != null ? fmtMoney(report.tariff) : null,
      platform?.valor_tarifa != null ? fmtMoney(platform.valor_tarifa) : null,
      compareScalar(report?.tariff, platform?.valor_tarifa, { mode: "text" }) === "match"
        ? "match"
        : amountClose(report?.tariff, platform?.valor_tarifa)
          ? "match"
          : report?.tariff != null && platform?.valor_tarifa == null
            ? "report_only"
            : platform?.valor_tarifa != null && report?.tariff == null
              ? "platform_only"
              : "mismatch"
    );
  }

  if (platform?.acrescimo_fp > 0 || platform?.desconto_fp > 0) {
    const fpParts = [];
    if (platform?.acrescimo_fp > 0) fpParts.push(`+${fmtMoney(platform.acrescimo_fp)}`);
    if (platform?.desconto_fp > 0) fpParts.push(`−${fmtMoney(platform.desconto_fp)}`);
    push(
      "fp",
      t("platformPage.passengerPartnerAuditCompare_fp"),
      null,
      fpParts.join(" "),
      "platform_only"
    );
  }

  if (platform?.forma_pagamento_nome) {
    push(
      "payment",
      t("platformPage.passengerPartnerAuditCompare_payment"),
      report?.payment_method,
      platform.forma_pagamento_nome,
      report?.payment_method ? compareScalar(report.payment_method, platform.forma_pagamento_nome, { mode: "name" }) : "platform_only"
    );
  }

  if (platform?.status_venda || report?.status) {
    push(
      "status",
      t("platformPage.passengerPartnerAuditCompare_status"),
      report?.status,
      platform?.status_venda,
      compareScalar(report?.status, platform?.status_venda, { mode: "name" })
    );
  }

  if (platform?.id) {
    push("sale_id", t("platformPage.passengerPartnerAuditCompare_sale_id"), null, `#${platform.id}`, "platform_only");
  }

  return rows;
}

const COMPARE_STATUS_META = {
  match: { color: "success", Icon: CheckCircleOutlineIcon },
  match_fp: { color: "success", Icon: CheckCircleOutlineIcon },
  swapped: { color: "info", Icon: SwapHorizIcon },
  mismatch: { color: "error", Icon: ErrorOutlineIcon },
  report_only: { color: "warning", Icon: RemoveIcon },
  platform_only: { color: "warning", Icon: RemoveIcon },
  partial: { color: "warning", Icon: RemoveIcon },
  neutral: { color: "default", Icon: RemoveIcon },
};

function CompareStatusCell({ status, hint, t }) {
  const meta = COMPARE_STATUS_META[status] || COMPARE_STATUS_META.neutral;
  const Icon = meta.Icon;
  return (
    <Stack spacing={0.25} alignItems="flex-start">
      <Chip
        size="small"
        color={meta.color === "default" ? "default" : meta.color}
        variant="outlined"
        icon={<Icon fontSize="small" />}
        label={t(`platformPage.passengerPartnerAuditCompareStatus_${status}`, { defaultValue: status })}
        sx={{ height: 24, "& .MuiChip-icon": { ml: 0.5 } }}
      />
      {hint ? (
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 160, lineHeight: 1.3 }}>
          {hint}
        </Typography>
      ) : null}
    </Stack>
  );
}

function CompareValueCell({ value, muted = false }) {
  if (value == null || value === "") {
    return (
      <Typography variant="body2" color="text.disabled" fontStyle="italic">
        —
      </Typography>
    );
  }
  return (
    <Typography variant="body2" color={muted ? "text.secondary" : "text.primary"} sx={{ wordBreak: "break-word", fontWeight: muted ? 400 : 500 }}>
      {value}
    </Typography>
  );
}

function AuditCompareTable({ rows, t }) {
  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("platformPage.passengerPartnerAuditDetailEmpty")}
      </Typography>
    );
  }

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "action.hover" }}>
            <TableCell width="18%">{t("platformPage.passengerPartnerAuditCompare_colField")}</TableCell>
            <TableCell width="30%">{t("platformPage.passengerPartnerAuditDetailReport")}</TableCell>
            <TableCell width="30%">{t("platformPage.passengerPartnerAuditDetailPlatform")}</TableCell>
            <TableCell width="22%">{t("platformPage.passengerPartnerAuditCompare_colStatus")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const isOk = ["match", "match_fp", "swapped"].includes(row.status);
            const isBad = row.status === "mismatch";
            return (
              <TableRow
                key={row.id}
                sx={(theme) => ({
                  bgcolor: isOk
                    ? alpha(theme.palette.success.main, 0.06)
                    : isBad
                      ? alpha(theme.palette.error.main, 0.07)
                      : row.status === "report_only" || row.status === "platform_only" || row.status === "partial"
                        ? alpha(theme.palette.warning.main, 0.05)
                        : undefined,
                })}
              >
                <TableCell>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {row.label}
                  </Typography>
                </TableCell>
                <TableCell>
                  <CompareValueCell value={row.reportVal} muted={row.status === "platform_only"} />
                </TableCell>
                <TableCell>
                  <CompareValueCell value={row.platformVal} muted={row.status === "report_only"} />
                </TableCell>
                <TableCell>
                  <CompareStatusCell status={row.status} hint={row.hint} t={t} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
}

function formatAggregateValue(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return fmtMoney(value);
  return String(value);
}

function DetailFieldList({ title, data, fields, t }) {
  const items = (fields || [])
    .map((key) => {
      const formatted = formatAggregateValue(data?.[key]);
      if (formatted == null) return null;
      return {
        key,
        label: t(`platformPage.passengerPartnerAuditDetail_${key}`, { defaultValue: key }),
        value: formatted,
      };
    })
    .filter(Boolean);

  if (!items.length) {
    return (
      <Box>
        {title ? (
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {title}
          </Typography>
        ) : null}
        <Typography variant="body2" color="text.secondary">
          {t("platformPage.passengerPartnerAuditDetailEmpty")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {title ? (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {title}
        </Typography>
      ) : null}
      <Stack spacing={0.75}>
        {items.map((item) => (
          <Box key={item.key}>
            <Typography variant="caption" color="text.secondary" display="block">
              {item.label}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
              {item.value}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function PartnerReportAuditEntryDialog({ selection, t, onClose }) {
  const open = Boolean(selection);
  const payload = selection?._payload;
  const report = payload?.report || null;
  const platform = payload?.platform || null;
  const isAggregate = selection?.type === "aggregate_amount_mismatch"
    || selection?.type === "aggregate_count_mismatch"
    || selection?.type === "daily_mismatch";

  const comparisonRows = useMemo(
    () => (report || platform ? buildComparisonRows(report, platform, t) : []),
    [report, platform, t]
  );

  const compareStats = useMemo(() => {
    const ok = comparisonRows.filter((r) => ["match", "match_fp", "swapped"].includes(r.status)).length;
    const bad = comparisonRows.filter((r) => r.status === "mismatch").length;
    const partial = comparisonRows.filter((r) => ["report_only", "platform_only", "partial"].includes(r.status)).length;
    return { ok, bad, partial };
  }, [comparisonRows]);

  const realIssues = useMemo(
    () => (payload?.issues || []).filter((x) => x.type !== "route_direction_swapped" && x.type !== "amount_payment_adjustment"),
    [payload?.issues]
  );

  const title = selection?.ticket_key
    || report?.ticket_key
    || platform?.localizador
    || platform?.numero_sistema
    || t("platformPage.passengerPartnerAuditDetailTitle");

  const issueChipColor = selection?.type === "unmatched_report"
    ? "error"
    : selection?.type === "unmatched_platform"
      ? "warning"
      : selection?.type?.includes("mismatch")
        ? "error"
        : "default";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="h6" component="span">
            {title}
          </Typography>
          {selection?.type ? (
            <Chip
              size="small"
              variant="outlined"
              color={issueChipColor}
              label={t(`platformPage.passengerPartnerAuditIssue_${selection.type}`, { defaultValue: selection.type })}
            />
          ) : null}
          {payload?.match_confidence ? (
            <Chip
              size="small"
              color="info"
              variant="outlined"
              label={t(`platformPage.passengerPartnerAuditConfidence_${payload.match_confidence}`, {
                score: payload.match_score,
                defaultValue: payload.match_confidence,
              })}
            />
          ) : null}
        </Stack>
        {!isAggregate && comparisonRows.length > 0 ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            <Chip size="small" color="success" variant="outlined" label={t("platformPage.passengerPartnerAuditCompareSummaryOk", { count: compareStats.ok })} />
            {compareStats.bad > 0 ? (
              <Chip size="small" color="error" variant="outlined" label={t("platformPage.passengerPartnerAuditCompareSummaryMismatch", { count: compareStats.bad })} />
            ) : null}
            {compareStats.partial > 0 ? (
              <Chip size="small" color="warning" variant="outlined" label={t("platformPage.passengerPartnerAuditCompareSummaryPartial", { count: compareStats.partial })} />
            ) : null}
          </Stack>
        ) : null}
        <IconButton aria-label={t("platformPage.passengerPartnerAuditDetailClose")} onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1.5 }}>
        {isAggregate ? (
          <DetailFieldList
            title={t("platformPage.passengerPartnerAuditDetailAggregate")}
            data={payload}
            fields={Object.keys(payload || {}).filter((k) => k !== "type")}
            t={t}
          />
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t("platformPage.passengerPartnerAuditCompareIntro")}
            </Typography>
            <AuditCompareTable rows={comparisonRows} t={t} />
          </>
        )}

        {payload?.match_key && !isAggregate ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="caption" color="text.secondary">
                {t("platformPage.passengerPartnerAuditDetailMatch")}:
              </Typography>
              <Chip size="small" variant="outlined" label={`${t("platformPage.passengerPartnerAuditDetailMatchKey")}: ${payload.match_key}`} />
              {payload.match_score != null ? (
                <Chip size="small" variant="outlined" label={`${t("platformPage.passengerPartnerAuditDetailMatchScore")}: ${payload.match_score}`} />
              ) : null}
            </Stack>
          </>
        ) : null}

        {realIssues.length > 0 ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("platformPage.passengerPartnerAuditDetailIssues")}
            </Typography>
            <Stack spacing={1}>
              {realIssues.map((issue, idx) => (
                <Alert key={`${issue.type}-${idx}`} severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {t(`platformPage.passengerPartnerAuditIssue_${issue.type}`, { defaultValue: issue.type })}
                  </Typography>
                  {issue.report != null || issue.platform != null ? (
                    <Typography variant="caption" display="block">
                      {t("platformPage.passengerPartnerAuditDetailReport")}: {issue.report ?? "—"} · {t("platformPage.passengerPartnerAuditDetailPlatform")}: {issue.platform ?? "—"}
                    </Typography>
                  ) : null}
                  {issue.report_amount != null ? (
                    <Typography variant="caption" display="block">
                      {fmtMoney(issue.report_amount)} × {fmtMoney(issue.platform_amount ?? issue.platform_valor_cobrado)}
                    </Typography>
                  ) : null}
                </Alert>
              ))}
            </Stack>
          </>
        ) : null}

        {(payload?.issues || []).some((x) => x.type === "amount_payment_adjustment") ? (
          <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
            {t("platformPage.passengerPartnerAuditCompareAmountFpOk")}
          </Alert>
        ) : null}

        {(payload?.issues || []).some((x) => x.type === "route_direction_swapped") ? (
          <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
            {t("platformPage.passengerPartnerAuditCompareRouteSwappedOk")}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">
          {t("platformPage.passengerPartnerAuditDetailClose")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || "");
      const base64 = raw.includes(",") ? raw.split(",")[1] : raw;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function PassengerPartnerReportAuditPanel({ dominioId, lockWorkspace = false, from = "", to = "" }) {
  const { t } = useTranslation("app");
  const [source, setSource] = useState("praxio_luna");
  const [file, setFile] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [workspaceOptions, setWorkspaceOptions] = useState([]);
  const [selectedDominioId, setSelectedDominioId] = useState(dominioId ? String(dominioId) : "");
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [chipFilter, setChipFilter] = useState(null);
  const tableRef = React.useRef(null);

  const stepDefs = useMemo(
    () => [
      { id: "parse", label: t("platformPage.passengerAuditStep_parse") },
      { id: "load_platform", label: t("platformPage.passengerAuditStep_load_platform") },
      { id: "compare", label: t("platformPage.passengerAuditStep_compare") },
    ],
    [t]
  );

  const stepLabels = useMemo(
    () => ({
      parse: t("platformPage.passengerAuditStep_parse"),
      load_platform: t("platformPage.passengerAuditStep_load_platform"),
      compare: t("platformPage.passengerAuditStep_compare"),
    }),
    [t]
  );

  const { steps: progressSteps, applyServerProgress } = useOperationProgress(stepDefs, loading);

  useEffect(() => {
    listPlatformDominios({ tenantLegacyStats: "0" })
      .then((res) => {
        const items = (res?.items || [])
          .filter((d) => String(d.workspace_segment || "").toLowerCase() === "passenger_transport")
          .map((d) => ({
            id: d.id,
            label: d.dominio || d.name || `#${d.id}`,
            schema: resolveTenantDatabaseName(d.dominio, d.bd),
          }));
        setWorkspaceOptions(items);
      })
      .catch(() => setWorkspaceOptions([]));
  }, []);

  useEffect(() => {
    if (dominioId != null && String(dominioId) !== "") {
      setSelectedDominioId(String(dominioId));
    }
  }, [dominioId]);

  const effectiveDominioId = selectedDominioId || (dominioId ? String(dominioId) : "");
  const selectedWorkspace = useMemo(
    () => workspaceOptions.find((w) => String(w.id) === String(effectiveDominioId)) || null,
    [workspaceOptions, effectiveDominioId]
  );

  const runAudit = async () => {
    if (!effectiveDominioId) {
      setError(t("platformPage.passengerPartnerAuditNeedWorkspace"));
      return;
    }
    if (!file) {
      setError(t("platformPage.passengerPartnerAuditNeedFile"));
      return;
    }
    setLoading(true);
    setError("");
    setAudit(null);
    setSelectedEntry(null);
    setChipFilter(null);
    try {
      const fileBase64 = await fileToBase64(file);
      const effectiveSource = detectSourceFromFile(file) || source;
      const data = await postPlatformDominioPassengerPartnerReportAudit(effectiveDominioId, {
        source: effectiveSource,
        fileName: file.name,
        fileBase64,
      });
      applyServerProgress(data?.progress, stepLabels);
      setAudit(data);
      if (!data?.ok && data?.message) {
        setError(data.message);
      }
    } catch (err) {
      setAudit(null);
      setError(err?.response?.data?.message || err?.message || t("platformPage.passengerPartnerAuditError"));
    } finally {
      setLoading(false);
    }
  };

  const auditRows = useMemo(() => {
    if (!audit) return [];
    const rows = [];

    if (audit.audit_mode === "aggregate") {
      for (const item of audit.aggregate_issues || []) {
        rows.push({
          _rowKey: `agg-${item.type}`,
          type: item.type,
          filterKeys: [item.type, "issues"],
          ticket_key: "—",
          localizador: "—",
          passenger: "—",
          total: item.report_amount ?? item.report_count,
          detail:
            item.type === "aggregate_amount_mismatch"
              ? `${fmtMoney(item.report_amount)} × ${fmtMoney(item.platform_amount)}`
              : `${fmtCount(item.report_count)} × ${fmtCount(item.platform_count)}`,
          _payload: item,
        });
      }
      for (const item of audit.daily_mismatches || []) {
        rows.push({
          _rowKey: `day-${item.sale_date}`,
          type: "daily_mismatch",
          filterKeys: ["daily_mismatch", "issues"],
          ticket_key: item.sale_date,
          localizador: "—",
          passenger: "—",
          total: item.report_total,
          detail: `${fmtCount(item.report_count)} bil. × ${fmtMoney(item.platform_total)} (${fmtCount(item.platform_count)} bil.)`,
          _payload: item,
        });
      }
      return rows;
    }

    for (const item of audit.matched || []) {
      const displayType = matchedRowType(item);
      rows.push({
        _rowKey: `m-${item.platform?.id}`,
        type: displayType,
        filterKeys: buildMatchedFilterKeys(item),
        ticket_key: item.report?.ticket_key || item.platform?.numero_sistema,
        localizador: item.report?.localizador || item.platform?.localizador,
        passenger: item.report?.passenger || item.platform?.nome_passageiro,
        total: item.report?.total ?? item.platform?.valor_cobrado,
        detail: matchedRowDetail(item, t),
        _payload: item,
      });
    }

    for (const item of audit.unmatched_report || []) {
      rows.push({
        _rowKey: `ur-${item.report?.ticket_key}`,
        type: "unmatched_report",
        filterKeys: ["unmatched_report", "report_rows", "issues"],
        ticket_key: item.report?.ticket_key,
        localizador: item.report?.localizador,
        passenger: item.report?.passenger,
        total: item.report?.total,
        detail: [item.report?.origem, item.report?.destino].filter(Boolean).join(" → "),
        _payload: item,
      });
    }

    for (const item of audit.unmatched_platform || []) {
      rows.push({
        _rowKey: `up-${item.platform?.id}`,
        type: "unmatched_platform",
        filterKeys: ["unmatched_platform", "platform_rows", "issues"],
        ticket_key: item.platform?.localizador || item.platform?.numero_sistema,
        localizador: item.platform?.localizador,
        passenger: item.platform?.nome_passageiro,
        total: item.platform?.valor_cobrado,
        detail: item.platform?.status_venda,
        _payload: item,
      });
    }

    return rows;
  }, [audit, t]);

  const filteredRows = useMemo(() => {
    if (!chipFilter) {
      return auditRows.filter((row) => row.filterKeys?.includes("issues"));
    }
    return auditRows.filter((row) => row.filterKeys?.includes(chipFilter));
  }, [auditRows, chipFilter]);

  const handleChipFilter = (key) => {
    setChipFilter((prev) => (prev === key ? null : key));
    requestAnimationFrame(() => {
      tableRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  const issueRows = auditRows.filter((row) => row.filterKeys?.includes("issues"));

  const summary = audit?.summary;
  const progressSummary = useMemo(() => {
    if (!progressSteps.length) return "";
    const done = progressSteps.filter((s) => ["ok", "error", "skipped"].includes(s.status)).length;
    return t("platformPage.passengerAuditProgressSummary", {
      done,
      total: progressSteps.length,
    });
  }, [progressSteps, t]);

  const tableTitle = useMemo(() => {
    if (!chipFilter) {
      return t("platformPage.passengerPartnerAuditIssuesTitle");
    }
    return t(`platformPage.passengerPartnerAuditFilterTitle_${chipFilter}`, {
      defaultValue: chipFilter,
    });
  }, [chipFilter, t]);

  const parseHintKey = audit?.hint || audit?.report_format || null;
  const parseHintText = parseHintKey
    ? t(`platformPage.passengerPartnerAuditHint_${parseHintKey}`, {
        defaultValue: audit?.message || "",
      })
    : "";

  return (
    <AnalyticsSection
      title={t("platformPage.passengerPartnerAuditTitle")}
      hint={t("platformPage.passengerPartnerAuditHint")}
      loading={false}
    >
      <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} alignItems={{ lg: "center" }} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 240 }} required>
          <InputLabel>{t("platformPage.productUsageColWorkspace")}</InputLabel>
          <Select
            label={t("platformPage.productUsageColWorkspace")}
            value={effectiveDominioId}
            disabled={lockWorkspace}
            onChange={(e) => {
              setSelectedDominioId(e.target.value);
              setFile(null);
              setAudit(null);
              setChipFilter(null);
              setError("");
            }}
          >
            <MenuItem value="">
              <em>{t("platformPage.passengerPartnerAuditSelectWorkspace")}</em>
            </MenuItem>
            {workspaceOptions.map((w) => (
              <MenuItem key={w.id} value={String(w.id)}>
                {w.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>{t("platformPage.passengerPartnerAuditSource")}</InputLabel>
          <Select
            label={t("platformPage.passengerPartnerAuditSource")}
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              setFile(null);
              setAudit(null);
            }}
          >
            {SOURCE_OPTIONS.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {t(`platformPage.passengerPartnerAuditSource_${opt.id}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />}>
          {file ? file.name : t("platformPage.passengerPartnerAuditChooseFile")}
          <input
            hidden
            type="file"
            accept={FILE_ACCEPT}
            onChange={(e) => {
              const nextFile = e.target.files?.[0] || null;
              const detected = detectSourceFromFile(nextFile);
              if (detected) setSource(detected);
              setFile(nextFile);
              setAudit(null);
              setChipFilter(null);
              setError("");
            }}
          />
        </Button>

        <Button
          size="small"
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FactCheckIcon />}
          onClick={runAudit}
          disabled={loading || !effectiveDominioId || !file}
        >
          {t("platformPage.passengerPartnerAuditRun")}
        </Button>
      </Stack>

      {selectedWorkspace ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          {t("platformPage.passengerPartnerAuditWorkspaceSelected", {
            workspace: selectedWorkspace.label,
            defaultValue: `Workspace: ${selectedWorkspace.label}`,
          })}
        </Typography>
      ) : null}

      {audit?.operator_key ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {t(`platformPage.passengerPartnerAuditErpGuide_${audit.operator_key}`, {
            defaultValue: audit.operator_key,
          })}
        </Alert>
      ) : null}

      {from || to ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          {t("platformPage.passengerPartnerAuditPeriodFilterHint", { from: from || "…", to: to || "…" })}
        </Typography>
      ) : null}

      {audit?.partner_filter_fallback ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {t("platformPage.passengerPartnerAuditPartnerFilterFallback")}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      {parseHintText && !audit?.ok ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {parseHintText}
        </Alert>
      ) : null}

      {(loading || progressSteps.length > 0) ? (
        <ConsoleOperationSteps running={loading} steps={progressSteps} summaryLabel={progressSummary} />
      ) : null}

      {summary ? (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Chip
              size="small"
              color={STATUS_COLOR[summary.status] || "default"}
              label={`${t("passagensPage.colStatus")}: ${summary.status}`}
            />
            {audit?.audit_mode === "aggregate" ? (
              <Chip size="small" color="info" variant="outlined" label={t("platformPage.passengerPartnerAuditModeAggregate")} />
            ) : (
              <Chip size="small" color="info" variant="outlined" label={t("platformPage.passengerPartnerAuditModeTicket")} />
            )}
            <AuditFilterChip
              filterKey="report_rows"
              active={chipFilter === "report_rows"}
              count={summary.report_rows}
              color="info"
              label={`${t("platformPage.passengerPartnerAuditReportRows")}: ${fmtCount(summary.report_rows)}`}
              onClick={handleChipFilter}
            />
            <AuditFilterChip
              filterKey="platform_rows"
              active={chipFilter === "platform_rows"}
              count={summary.platform_rows}
              color="info"
              label={`${t("platformPage.passengerPartnerAuditPlatformRows")}: ${fmtCount(summary.platform_rows)}`}
              onClick={handleChipFilter}
            />
            {audit?.audit_mode === "aggregate" ? (
              <>
                <Chip
                  size="small"
                  color={summary.aggregate_count_mismatch ? "warning" : "success"}
                  variant="outlined"
                  label={`${t("platformPage.passengerPartnerAuditCountDelta")}: ${fmtCount((summary.report_rows || 0) - (summary.platform_rows || 0))}`}
                />
                <AuditFilterChip
                  filterKey="daily_mismatch"
                  active={chipFilter === "daily_mismatch"}
                  count={summary.daily_mismatch_count}
                  color="warning"
                  label={`${t("platformPage.passengerPartnerAuditDailyMismatch")}: ${fmtCount(summary.daily_mismatch_count)}`}
                  onClick={handleChipFilter}
                />
              </>
            ) : (
              <>
                <AuditFilterChip
                  filterKey="matched"
                  active={chipFilter === "matched"}
                  count={summary.matched_count}
                  color="success"
                  label={`${t("platformPage.passengerMpAuditMatched")}: ${fmtCount(summary.matched_count)}`}
                  onClick={handleChipFilter}
                />
                <AuditFilterChip
                  filterKey="exact_match"
                  active={chipFilter === "exact_match"}
                  count={summary.exact_match_count}
                  color="success"
                  label={`${t("platformPage.passengerPartnerAuditExactMatches")}: ${fmtCount(summary.exact_match_count)}`}
                  onClick={handleChipFilter}
                />
                <AuditFilterChip
                  filterKey="probable_match"
                  active={chipFilter === "probable_match"}
                  count={summary.probable_match_count}
                  color="warning"
                  label={`${t("platformPage.passengerPartnerAuditProbableMatches")}: ${fmtCount(summary.probable_match_count)}`}
                  onClick={handleChipFilter}
                />
                <AuditFilterChip
                  filterKey="unmatched_report"
                  active={chipFilter === "unmatched_report"}
                  count={summary.unmatched_report_count}
                  color="warning"
                  label={`${t("platformPage.passengerPartnerAuditUnmatchedReport")}: ${fmtCount(summary.unmatched_report_count)}`}
                  onClick={handleChipFilter}
                />
                <AuditFilterChip
                  filterKey="unmatched_platform"
                  active={chipFilter === "unmatched_platform"}
                  count={summary.unmatched_platform_count}
                  color="warning"
                  label={`${t("platformPage.passengerPartnerAuditUnmatchedPlatform")}: ${fmtCount(summary.unmatched_platform_count)}`}
                  onClick={handleChipFilter}
                />
                <AuditFilterChip
                  filterKey="amount_mismatch"
                  active={chipFilter === "amount_mismatch"}
                  count={summary.amount_mismatch_count}
                  color="error"
                  label={`${t("platformPage.passengerMpAuditAmountMismatch")}: ${fmtCount(summary.amount_mismatch_count)}`}
                  onClick={handleChipFilter}
                />
                {summary.field_mismatch_count > 0 ? (
                  <AuditFilterChip
                    filterKey="field_mismatch"
                    active={chipFilter === "field_mismatch"}
                    count={summary.field_mismatch_count}
                    color="error"
                    label={`${t("platformPage.passengerPartnerAuditFieldMismatch")}: ${fmtCount(summary.field_mismatch_count)}`}
                    onClick={handleChipFilter}
                  />
                ) : null}
              </>
            )}
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip size="small" variant="outlined" label={`${t("platformPage.passengerPartnerAuditReportTotal")}: ${fmtMoney(summary.report_total_amount)}`} />
            <Chip size="small" variant="outlined" label={`${t("platformPage.passengerPartnerAuditPlatformTotal")}: ${fmtMoney(summary.platform_total_amount)}`} />
            {audit?.operator_key ? (
              <Chip
                size="small"
                variant="outlined"
                color="info"
                label={`${t("platformPage.passengerPartnerAuditOperator")}: ${t(`platformPage.passengerPartnerAuditOperator_${audit.operator_key}`, { defaultValue: audit.operator_key })}`}
              />
            ) : null}
            {audit?.period_info?.report_period?.from ? (
              <Chip
                size="small"
                variant="outlined"
                color="info"
                label={`${t("platformPage.passengerPartnerAuditReportPeriod")}: ${audit.period_info.report_period.from} → ${audit.period_info.report_period.to || audit.period_info.report_period.from}`}
              />
            ) : null}
            {audit?.period?.from ? (
              <Chip
                size="small"
                variant="outlined"
                label={`${t("platformPage.passengerPartnerAuditQueryPeriod")}: ${audit.period.from} → ${audit.period.to || audit.period.from}`}
              />
            ) : null}
            {audit?.period_info?.ui_filter_ignored ? (
              <Chip size="small" color="warning" variant="outlined" label={t("platformPage.passengerPartnerAuditUiFilterIgnored")} />
            ) : null}
            {audit?.period_info?.source === "ui_filter" ? (
              <Chip size="small" color="info" variant="outlined" label={t("platformPage.passengerPartnerAuditUiFilterUsed")} />
            ) : null}
            {summary.parse_errors > 0 ? (
              <Chip size="small" color="warning" variant="outlined" label={`${t("platformPage.passengerPartnerAuditParseErrors")}: ${summary.parse_errors}`} />
            ) : null}
          </Stack>
        </>
      ) : null}

      {audit?.aggregate_sections?.length > 0 ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t("platformPage.passengerPartnerAuditSectionsTitle")}
          </Typography>
          <AnalyticsDataTable
            rows={audit.aggregate_sections.map((s, i) => ({
              _rowKey: `sec-${i}`,
              establishment: s.establishment,
              empresa: s.empresa,
              ticket_count: s.ticket_count,
              total_amount: s.total_amount,
            }))}
            emptyLabel={t("platformPage.passengerPartnerAuditIssuesEmpty")}
            columns={[
              { id: "establishment", label: t("platformPage.passengerPartnerAuditColEstablishment"), render: (row) => row.establishment || "—" },
              { id: "empresa", label: t("platformPage.passengerPartnerAuditColCompany"), render: (row) => row.empresa || "—" },
              { id: "ticket_count", label: t("platformPage.passengerPartnerAuditReportRows"), align: "right", render: (row) => fmtCount(row.ticket_count) },
              { id: "total_amount", label: t("passagensPage.colValue"), align: "right", render: (row) => fmtMoney(row.total_amount) },
            ]}
          />
        </Box>
      ) : null}

      {(chipFilter || issueRows.length > 0) ? (
        <Box ref={tableRef}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {tableTitle}
              {" · "}
              {t("platformPage.passengerPartnerAuditRowClickHint")}
            </Typography>
            {chipFilter ? (
              <Chip
                size="small"
                variant="outlined"
                label={t("platformPage.passengerPartnerAuditFilterClear")}
                onClick={() => setChipFilter(null)}
                onDelete={() => setChipFilter(null)}
              />
            ) : null}
            {chipFilter ? (
              <Typography variant="caption" color="text.secondary">
                {t("platformPage.passengerPartnerAuditFilterCount", { count: fmtCount(filteredRows.length) })}
              </Typography>
            ) : null}
          </Stack>
          <AnalyticsDataTable
            rows={filteredRows}
            emptyLabel={
              chipFilter
                ? t("platformPage.passengerPartnerAuditFilterEmpty")
                : t("platformPage.passengerPartnerAuditIssuesEmpty")
            }
            onRowClick={(row) => setSelectedEntry(row)}
            columns={[
              {
                id: "type",
                label: t("platformPage.passengerMpAuditColType"),
                render: (row) => (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={
                      row.type === "unmatched_report"
                        ? "error"
                        : row.type === "unmatched_platform"
                          ? "warning"
                          : row.type === "matched" || row.type === "probable_match"
                            ? "success"
                            : "default"
                    }
                    label={t(`platformPage.passengerPartnerAuditIssue_${row.type}`, { defaultValue: row.type })}
                  />
                ),
              },
              { id: "ticket_key", label: t("platformPage.passengerPartnerAuditColTicket"), render: (row) => row.ticket_key || "—" },
              { id: "localizador", label: t("passagensPage.colLocator", { defaultValue: "Localizador" }), render: (row) => row.localizador || "—" },
              { id: "passenger", label: t("passagensPage.colClient"), render: (row) => row.passenger || "—" },
              { id: "total", label: t("passagensPage.colValue"), align: "right", render: (row) => fmtMoney(row.total) },
              { id: "detail", label: t("platformPage.passengerPartnerAuditColDetail"), render: (row) => row.detail || "—" },
            ]}
          />
        </Box>
      ) : null}

      {!chipFilter && issueRows.length === 0 && summary?.status === "ok" ? (
        <Alert severity="success">{t("platformPage.passengerPartnerAuditOk")}</Alert>
      ) : null}

      <PartnerReportAuditEntryDialog
        selection={selectedEntry}
        t={t}
        onClose={() => setSelectedEntry(null)}
      />
    </AnalyticsSection>
  );
}
